import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { sortBy } from 'lodash';
import {
  Extension,
  getAllExtensions,
  getExtensionRegistrationData,
  ExtensionRegistrationData,
} from '../../api/ExtensionService';
import { StoreState } from '../../store/store';

export const fetchExtensions = createAsyncThunk<
  Extension[],
  undefined,
  {
    state: StoreState;
  }
>('extension/fetchExtensions', async () => {
  const publicData = await getAllExtensions(true);
  const privateData = await getAllExtensions(false);
  return [...publicData, ...privateData];
});

export const fetchExtensionRegistrationData = createAsyncThunk<
  ExtensionRegistrationData,
  undefined,
  {
    state: StoreState;
  }
>('extension/fetchPluginData', async () => {
  const data = await getExtensionRegistrationData();
  return data;
});

export interface ExtensionsState {
  allExtensions: Extension[];
  extensionRegistrationData: ExtensionRegistrationData;
}

const initialState: ExtensionsState = {
  allExtensions: [],
  extensionRegistrationData: {},
};

export const selectAllExtensions = createSelector(
  (state: StoreState) => state.extensions.allExtensions,
  (allExtensions: Extension[]) =>
    sortBy(allExtensions, [(e: Extension) => e.displayName.toUpperCase()])
);

export const selectExtensionRegistrationData = (state: StoreState) => {
  return state.extensions.extensionRegistrationData;
};

export const selectExtensionByUUID = (uuid: string) => (state: StoreState) => {
  let extension: Extension = {
    objectUUID: '',
    name: '',
    displayName: '',
  };

  state.extensions.allExtensions.find(item => {
    if (item.objectUUID === uuid) {
      extension = item;
      return true;
    }
    return false;
  });

  return extension;
};

const { reducer } = createSlice({
  name: 'extensions',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchExtensions.pending, state => ({
      ...state,
      allExtensions: [],
    }));
    builder.addCase(fetchExtensions.rejected, state => ({
      ...state,
      allExtensions: [],
    }));
    builder.addCase(fetchExtensions.fulfilled, (state, action) => {
      return {
        ...state,
        allExtensions: action.payload,
      };
    });
    builder.addCase(
      fetchExtensionRegistrationData.fulfilled,
      (state, action) => {
        state.extensionRegistrationData = action.payload;
      }
    );
    builder.addCase(fetchExtensionRegistrationData.rejected, state => {
      state.extensionRegistrationData = {};
    });
    builder.addCase(fetchExtensionRegistrationData.pending, state => {
      state.extensionRegistrationData = {};
    });
  },
});

export default reducer;
